import { useEffect, useState } from 'react'
import {Award, BookOpen, Calendar, Download, ExternalLink, Search, CheckCircle2, Clock} from 'lucide-react'
import ColaboradorLayout from '@/components/ColaboradorLayout'
import { useAuth } from '@/hooks/useAuth'
import {
  useTrainings,
  type Enrollment,
  type Training,
  TRAINING_TYPE_LABELS,
  TRAINING_MODALITY_LABELS,
} from '@/hooks/useTrainings'

type FilterType = 'todos' | 'curso' | 'taller' | 'seminario' | 'certificacion'

function CertificateBadge({ type }: { type: Training['type'] }) {
  const colors: Record<Training['type'], string> = {
    curso: 'bg-blue-100 text-blue-700',
    taller: 'bg-violet-100 text-violet-700',
    seminario: 'bg-amber-100 text-amber-700',
    certificacion: 'bg-emerald-100 text-emerald-700',
  }
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${colors[type] ?? 'bg-slate-100 text-slate-600'}`}>
      {TRAINING_TYPE_LABELS[type] ?? type}
    </span>
  )
}

function CertificateCard({ enrollment, training }: { enrollment: Enrollment; training?: Training }) {
  const completedDate = enrollment.completedAt
    ? new Date(enrollment.completedAt).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })
    : enrollment.updatedAt
    ? new Date(enrollment.updatedAt).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })
    : '—'

  const trainingType = (enrollment.trainingType as Training['type']) ?? 'curso'

  return (
    <div className="group relative flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:border-amber-300 hover:shadow-md">
      {/* Header decorativo */}
      <div className="relative flex h-28 items-center justify-center bg-gradient-to-br from-slate-900 to-slate-700 px-6">
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,.05) 10px, rgba(255,255,255,.05) 20px)' }}
        />
        <Award size={44} className="text-amber-400 drop-shadow" />
        <div className="absolute right-3 top-3">
          <CertificateBadge type={trainingType} />
        </div>
      </div>

      {/* Contenido */}
      <div className="flex flex-1 flex-col gap-3 p-5">
        <div>
          <h3 className="text-sm font-bold text-slate-900 leading-snug line-clamp-2">
            {enrollment.trainingTitle ?? training?.title ?? 'Capacitación'}
          </h3>
          {training?.instructor ? (
            <p className="mt-0.5 text-xs text-slate-400">Instructor: {training.instructor}</p>
          ) : null}
        </div>

        <div className="flex flex-col gap-1.5 text-xs text-slate-500">
          <div className="flex items-center gap-1.5">
            <CheckCircle2 size={13} className="text-emerald-500 shrink-0" />
            <span className="font-medium text-emerald-600">Completado</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Calendar size={13} className="shrink-0" />
            <span>{completedDate}</span>
          </div>
          {training?.modality ? (
            <div className="flex items-center gap-1.5">
              <BookOpen size={13} className="shrink-0" />
              <span>{TRAINING_MODALITY_LABELS[training.modality] ?? training.modality}</span>
            </div>
          ) : null}
        </div>

        <div className="mt-auto flex gap-2">
          {enrollment.certificateUrl ? (
            <>
              <a
                href={enrollment.certificateUrl}
                target="_blank"
                rel="noreferrer"
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-amber-400 px-3 py-2 text-xs font-semibold text-slate-900 transition hover:bg-amber-500"
              >
                <Download size={13} />
                Descargar
              </a>
              <a
                href={enrollment.certificateUrl}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-center rounded-lg border border-slate-200 px-2.5 py-2 text-slate-500 transition hover:bg-slate-50"
              >
                <ExternalLink size={13} />
              </a>
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-xs text-slate-400">
              <Clock size={13} />
              Certificado en proceso
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function ColaboradorCertificados() {
  const { user } = useAuth()
  const { enrollments, trainings, loading, fetchMyEnrollments, fetchPublished } = useTrainings()
  const [filter, setFilter] = useState<FilterType>('todos')
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (user?.userId) {
      Promise.all([fetchMyEnrollments(user.userId), fetchPublished()])
    }
  }, [user?.userId])

  const completed = enrollments.filter((e) => e.status === 'completado')

  const filtered = completed.filter((e) => {
    const matchType = filter === 'todos' || e.trainingType === filter
    const matchSearch =
      !search ||
      (e.trainingTitle ?? '').toLowerCase().includes(search.toLowerCase())
    return matchType && matchSearch
  })

  const getTraining = (id: string) => trainings.find((t) => t._id === id)

  const filterTabs: { key: FilterType; label: string }[] = [
    { key: 'todos', label: 'Todos' },
    { key: 'certificacion', label: 'Certificaciones' },
    { key: 'curso', label: 'Cursos' },
    { key: 'taller', label: 'Talleres' },
    { key: 'seminario', label: 'Seminarios' },
  ]

  return (
    <ColaboradorLayout
      title="Mis certificados"
      subtitle="Certificados digitales obtenidos al completar capacitaciones en Beeznz Work"
    >
      {/* KPIs */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: 'Total obtenidos', value: completed.length, color: 'text-emerald-600', bg: 'bg-emerald-50' },
          { label: 'Certificaciones', value: completed.filter((e) => e.trainingType === 'certificacion').length, color: 'text-violet-600', bg: 'bg-violet-50' },
          { label: 'Cursos', value: completed.filter((e) => e.trainingType === 'curso').length, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Talleres', value: completed.filter((e) => e.trainingType === 'taller').length, color: 'text-amber-600', bg: 'bg-amber-50' },
        ].map(({ label, value, color, bg }) => (
          <div key={label} className="rounded-xl border border-slate-200 bg-white p-4">
            <p className={`text-2xl font-bold ${color}`}>{loading ? '—' : value}</p>
            <p className="mt-0.5 text-xs text-slate-500">{label}</p>
          </div>
        ))}
      </div>

      {/* Controles */}
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-1.5">
          {filterTabs.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                filter === key
                  ? 'bg-amber-400 text-slate-900'
                  : 'border border-slate-200 bg-white text-slate-600 hover:border-amber-300'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar certificado…"
            className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-8 pr-4 text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100 sm:w-56"
          />
        </div>
      </div>

      {/* Grid de certificados */}
      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-64 animate-pulse rounded-2xl bg-slate-100" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-200 py-20 text-center">
          <Award size={40} className="mb-3 text-slate-300" />
          <p className="font-semibold text-slate-600">
            {completed.length === 0
              ? 'Aún no tienes certificados'
              : 'Sin resultados para los filtros aplicados'}
          </p>
          <p className="mt-1 text-sm text-slate-400">
            {completed.length === 0
              ? 'Completa una capacitación para obtener tu primer certificado digital.'
              : 'Prueba con otro tipo o limpia la búsqueda.'}
          </p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((enrollment) => (
            <CertificateCard
              key={enrollment._id}
              enrollment={enrollment}
              training={getTraining(enrollment.trainingId)}
            />
          ))}
        </div>
      )}

      {/* Progreso general */}
      {enrollments.length > 0 && (
        <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-5">
          <h2 className="mb-4 text-sm font-semibold text-slate-900">Progreso general de capacitaciones</h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 text-center">
            {[
              { label: 'Inscrito', count: enrollments.filter((e) => e.status === 'inscrito').length, color: 'text-amber-600' },
              { label: 'En progreso', count: enrollments.filter((e) => e.status === 'en_progreso').length, color: 'text-blue-600' },
              { label: 'Completado', count: enrollments.filter((e) => e.status === 'completado').length, color: 'text-emerald-600' },
              { label: 'Cancelado', count: enrollments.filter((e) => e.status === 'cancelado').length, color: 'text-slate-400' },
            ].map(({ label, count, color }) => (
              <div key={label} className="rounded-xl bg-slate-50 p-3">
                <p className={`text-xl font-bold ${color}`}>{count}</p>
                <p className="text-xs text-slate-500">{label}</p>
              </div>
            ))}
          </div>
          <div className="mt-4">
            <div className="mb-1 flex justify-between text-xs text-slate-500">
              <span>Progreso total</span>
              <span>{enrollments.length > 0 ? Math.round((completed.length / enrollments.length) * 100) : 0}% completado</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full rounded-full bg-amber-400 transition-all"
                style={{ width: `${enrollments.length > 0 ? (completed.length / enrollments.length) * 100 : 0}%` }}
              />
            </div>
          </div>
        </div>
      )}
    </ColaboradorLayout>
  )
}

export default ColaboradorCertificados
